import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from 'libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { Table } from 'antd';
import Text from 'antd/es/typography/Text';
import { fileNameEdit } from 'helpers/FileUtils';
import _ from 'lodash';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/sales/');

const format = (v) => `${v}%`;

const defaultState = {
  tableData: [],
  lineChartData: [],
  barChart: [],
  barChangeLine: [],
  total: {},
};

const useInvoicesSalesVillage = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { handleDownloadPdf, loadingPdf } = usePdf();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const summary = useCallback(() => {
    const { total = {} } = data;

    delete total.Periodo;

    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <>
        <Table.Summary.Row fixed>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </>
    );
  }, [data]);

  const [columns, setColumns] = useState([
    {
      title: t('Periodo'),
      dataIndex: 'Periodo',
      fixed: true,
    },
    {
      title: t('Net Sales YTD'),
      dataIndex: 'Net Sales YTD',
      render: (v) => Helpers.numberWithCommas(v, ' €'),
    },
    {
      title: t('Net Sales LY'),
      dataIndex: 'Net Sales LY',
      render: (v) => Helpers.numberWithCommas(v, ' €'),
    },
    {
      title: t('Net Sales vs LY (+/-%)'),
      dataIndex: 'Net Sales vs LY (+/-%)',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Incidenza % Net Sales YTD'),
      dataIndex: 'Incidenza % Net Sales YTD',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Incidenza % Net Sales LY'),
      dataIndex: 'Incidenza % Net Sales LY',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
  ]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;
        let data, total;
        if (!isAllReports) {
          const {
            data: { data: backData, total: backTotal },
          } = await api.getFootfalls({ params });
          data = backData;
          total = backTotal;
        } else {
          const { data: backData, total: backTotal } = allReportsData;
          data = backData;
          total = backTotal;
        }

        if (!data) return;

        const tableKeys = Object.keys(total);
        const filteredColumns = columns.filter((column) =>
          tableKeys.includes(column.dataIndex),
        );
        const lineChartData1 = {
          id: 'Net Sales LY',
          // color: 'blue',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Net Sales LY'],
          })),
        };
        const lineChartData2 = {
          id: 'Net Sales YTD',
          // color: 'blue',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Net Sales YTD'],
          })),
        };

        const maxValueFroLy = _.maxBy(data, 'Incidenza % Net Sales LY')[
          'Incidenza % Net Sales LY'
        ];
        const maxValueFroYtd = _.maxBy(data, 'Incidenza % Net Sales YTD')[
          'Incidenza % Net Sales YTD'
        ];

        const maxValue =
          maxValueFroLy > maxValueFroYtd ? maxValueFroLy : maxValueFroYtd;

        const barChart = data.map((item) => ({
          date: item['Periodo'],
          'Net Sales LY': item['Incidenza % Net Sales LY'],
          'Net Sales LYColor': '#61cdbb',
          'Net Sales YTD': item['Incidenza % Net Sales YTD'],
          'Net Sales YTDColor': '#f47560',
        }));

        const barChangeLine = {
          id: 'Net Sales vs LY (+/-%)',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Net Sales vs LY (+/-%)'],
            tooltipKeys: [
              {
                key: 'Net Sales LY',
                value: Helpers.changeDotToComa(
                  format(item['Incidenza % Net Sales LY']),
                ),
                color: '#61cdbb',
                percent: '',
              },
              {
                key: 'Net Sales YTD',
                value: Helpers.changeDotToComa(
                  format(item['Incidenza % Net Sales YTD']),
                ),
                color: '#f47560',
                percent: '',
              },
              {
                key: 'Net Sales vs LY (+/-%)',
                value: Helpers.changeDotToComa(
                  format(item['Net Sales vs LY (+/-%)']),
                ),
                color: '#1f77b4',
                percent: '',
              },
            ],
          })),
        };

        setData({
          tableData: data,
          lineChartData: [lineChartData1, lineChartData2],
          barChart,
          maxValue,
          barChangeLine: [barChangeLine],
          total,
        });
        setColumns(filteredColumns);
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setData(defaultState);
        setErrors(UIHelper.showAnalyticsError(e));
      } finally {
        setLoading(false);
        if (isPrint) {
          const event = new CustomEvent('status', { detail: 'ok' });

          setTimeout(() => {
            window.dispatchEvent(event);
          }, 5000);
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  return {
    columns,
    data,
    filters,
    format,
    title,
    loading,
    handleDownloadExcel,
    summary,
    searchParams,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useInvoicesSalesVillage;
